import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StepReportService } from '@pulpo/pulpo-api';
import type {
  UserRequestStat,
  UserRequestStatScope,
} from '@pulpo/pulpo-models';
import {
  UserRequestLinesDialogComponent,
  UserRequestLinesDialogData,
} from './user-request-lines-dialog.component';
import { canalLabel, requesterDisplayName } from './user-request-stats.labels';

/**
 * Requests per requester (PUL-3237): a user on the rest canal, or the
 * requester code received on the xml canal. Selecting a requester opens what
 * it asked for (product, dates, cities, travellers) in a dialog.
 */
@Component({
  selector: 'pulpo-user-request-stats',
  templateUrl: './user-request-stats.component.html',
  styleUrls: ['./user-request-stats.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    MatButtonToggleModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class UserRequestStatsComponent implements OnInit {
  private readonly stepReportService = inject(StepReportService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  /** 'admin' sees every requester, 'producer' only its own products'. */
  @Input() scope: UserRequestStatScope = 'producer';
  /** Adds buttons opening the request and response payloads (admin only). */
  @Input() showPayloads = false;

  readonly periods = [7, 30, 92];
  readonly statColumns = [
    'requester',
    'groups',
    'canal',
    'list',
    'quote',
    'booking',
    'lastRequestDate',
  ];

  days = 30;
  search = '';
  canal?: string;
  sort: Sort = { active: 'lastRequestDate', direction: 'desc' };
  stats: UserRequestStat[] = [];
  filteredStats: UserRequestStat[] = [];
  loadingStats = false;

  ngOnInit(): void {
    this.loadStats();
  }

  onPeriodChange(days: number): void {
    this.days = days;
    this.loadStats();
  }

  onSearchChange(): void {
    const term = this.search.trim().toLowerCase();
    const filtered = this.stats.filter(
      (stat) =>
        (!this.canal || stat.canal === this.canal) &&
        (!term ||
          [
            this.displayName(stat),
            stat.login,
            stat.requester,
            ...(stat.groups || []),
          ].some((value) => value?.toLowerCase().includes(term)))
    );
    this.filteredStats = this.sorted(filtered);
  }

  onCanalChange(): void {
    this.onSearchChange();
  }

  onSortChange(sort: Sort): void {
    this.sort = sort;
    this.onSearchChange();
  }

  open(stat: UserRequestStat): void {
    const data: UserRequestLinesDialogData = {
      stat,
      scope: this.scope,
      days: this.days,
      showPayloads: this.showPayloads,
    };
    this.dialog.open(UserRequestLinesDialogComponent, {
      data,
      width: '90vw',
      maxWidth: '1400px',
      maxHeight: '90vh',
    });
  }

  displayName(stat: UserRequestStat): string {
    return requesterDisplayName(stat);
  }

  secondaryName(stat: UserRequestStat): string | undefined {
    const name = this.displayName(stat);
    return stat.login && stat.login !== name ? stat.login : undefined;
  }

  get canalOptions(): { value: string; label: string }[] {
    return ['rest', 'xml'].map((value) => ({
      value,
      label: this.canalLabel(value),
    }));
  }

  canalLabel(canal: string): string {
    return canalLabel(this.scope, canal);
  }

  /** Client side: every requester of the period is already loaded. */
  private sorted(stats: UserRequestStat[]): UserRequestStat[] {
    const { active, direction } = this.sort;
    if (!active || !direction) {
      return stats;
    }
    const factor = direction === 'asc' ? 1 : -1;
    return [...stats].sort((a, b) => {
      const left = this.sortValue(a, active);
      const right = this.sortValue(b, active);
      if (left === right) {
        return 0;
      }
      // empty values always last, whatever the direction
      if (left === undefined || left === '') {
        return 1;
      }
      if (right === undefined || right === '') {
        return -1;
      }
      return (left < right ? -1 : 1) * factor;
    });
  }

  private sortValue(
    stat: UserRequestStat,
    column: string
  ): string | number | undefined {
    switch (column) {
      case 'requester':
        return this.displayName(stat).toLowerCase();
      case 'groups':
        return (stat.groups || []).join(', ').toLowerCase();
      case 'canal':
        return this.canalLabel(stat.canal).toLowerCase();
      // on the figure shown, the OK count
      case 'list':
        return stat.listOk;
      case 'quote':
        return stat.quoteOk;
      case 'booking':
        return stat.bookingOk;
      case 'lastRequestDate':
        return stat.lastRequestDate;
      default:
        return undefined;
    }
  }

  private loadStats(): void {
    this.loadingStats = true;
    this.stepReportService
      .getUserRequestStats(this.scope, this.days)
      .subscribe({
        next: (stats) => {
          this.stats = stats || [];
          this.onSearchChange();
          this.loadingStats = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.stats = [];
          this.filteredStats = [];
          this.loadingStats = false;
          this.cdr.markForCheck();
        },
      });
  }
}
